import { useCallback, useEffect, useState } from 'react';
import { QueryResult } from './queryResult';
import { DeliveryTrackerRequestPayload } from './adapters/deliveryTracker';
import { RawDeliveryTrackerResponse } from './types';

// Mirrors the real production Purchase History experience app's own delivery-tracker endpoint
// (ocm_pd_experience_customer-account-orders-purchases: OMS_DELIVERY_TRACKER_PATH,
// GetDeliveryTrackerAPI) — a REST call entirely separate from orderDetails/purchaseHistory, same
// precedent as useOrderFilterOptionsQuery.ts for OMS endpoints @thd-nucleus/data-sources
// (GraphQL-only) can't reach. This has not been confirmed reachable from this component's own
// environment — it's a best-effort mirror of that real app's contract, not a verified live
// endpoint, and it depends on orderDetailsDataModel.ts's own unconfirmed trackingNumber/
// workOrderNumber fields (see that file's header comment) actually resolving to real values.
export const OMS_DELIVERY_TRACKER_PATH = (userId: string) => `/oms/customer/order/v1/user/${userId}/deliveryTracker`;

export interface UseDeliveryTrackerQueryOptions {
  /** True whenever the caller hasn't determined a request to send yet — see
   *  isDeliveryTrackerEligible/buildDeliveryTrackerRequest (adapters/deliveryTracker.ts). Mirrors
   *  useOrderDetailsQuery's own skip param: no request is ever sent while true. */
  skip?: boolean;
}

/**
 * Fetches live carrier-tracking status/links for one fulfillment group, gated by the caller on
 * isDeliveryTrackerEligible. Auto-fetches on mount and whenever the identifying request fields
 * change (matching useOrderFilterOptionsQuery's own auto-fetch-on-dependency-change behavior)
 * rather than requiring an explicit trigger, since ExpandedOrderDetailsPanel wants this data ready
 * as soon as it's eligible, the same way it already treats orderDetails itself.
 */
export function useDeliveryTrackerQuery(
  userId: string,
  request: DeliveryTrackerRequestPayload | null,
  { skip = false }: UseDeliveryTrackerQueryOptions = {}
): QueryResult<RawDeliveryTrackerResponse> {
  const [result, setResult] = useState<QueryResult<RawDeliveryTrackerResponse>>({ status: 'idle' });
  const { orderNumber, trackingNumber, workOrderNumber, fulfillmentType } = request?.deliveryTrackerRequest ?? {};

  const fetchDeliveryTracker = useCallback(async () => {
    if (!request) {
      setResult({ status: 'idle' });
      return;
    }

    setResult({ status: 'loading' });
    try {
      const response = await fetch(OMS_DELIVERY_TRACKER_PATH(userId), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        throw new Error(`Delivery tracker request failed with status ${response.status}`);
      }

      const data: RawDeliveryTrackerResponse = await response.json();
      setResult({ status: 'success', data, refetch: fetchDeliveryTracker });
    } catch (error) {
      setResult({
        status: 'error',
        error: error instanceof Error ? error : new Error('Unknown error fetching delivery tracker status'),
        refetch: fetchDeliveryTracker
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, orderNumber, trackingNumber, workOrderNumber, fulfillmentType]);

  useEffect(() => {
    if (skip || !request) {
      setResult({ status: 'idle' });
      return;
    }
    fetchDeliveryTracker();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip, fetchDeliveryTracker]);

  return result;
}
